// ** React Import
import { useEffect, useRef } from 'react';

// ** MUI Imports
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Type Import
import type { LayoutProps } from '../types';

// ** Layout Components
import VerticalLayout from './VerticalLayout';
import HorizontalLayout from './HorizontalLayout';

const Layout = (props: LayoutProps) => {
  'use memo';

  // ** Props
  const { hidden, children, settings, saveSettings } = props;

  // ** Hooks
  const theme = useTheme();
  const compactNav = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });

  // ** Ref
  const isCollapsed = useRef(settings.navCollapsed);

  useEffect(() => {
    saveSettings((current) => {
      if (current.layout !== 'horizontal' || current.menuTextHidden === compactNav) {
        return current;
      }
      return { ...current, menuTextHidden: compactNav };
    });
  }, [compactNav, saveSettings]);

  useEffect(() => {
    saveSettings((current) => {
      if (hidden) {
        if (current.layout === 'horizontal') {
          isCollapsed.current = true;
          return { ...current, navCollapsed: false, layout: 'vertical' };
        }

        if (!current.navCollapsed) {
          return current;
        }

        isCollapsed.current = true;
        return { ...current, navCollapsed: false, layout: 'vertical' };
      }

      if (isCollapsed.current) {
        isCollapsed.current = false;
        return {
          ...current,
          navCollapsed: true,
          layout: current.lastLayout ?? 'vertical',
        };
      }

      if (current.lastLayout !== current.layout && current.lastLayout !== undefined) {
        return { ...current, layout: current.lastLayout };
      }

      return current;
    });
  }, [hidden, saveSettings]);

  if (settings.layout === 'horizontal') {
    return <HorizontalLayout {...props}>{children}</HorizontalLayout>;
  }

  return <VerticalLayout {...props}>{children}</VerticalLayout>;
};

export default Layout;
